import {
    CloudDownloadOutlined,
    LoadingOutlined,
    UploadOutlined,
} from '@ant-design/icons';
import { Button, Space, Spin, Table, Tooltip, Typography, Upload } from 'antd';
import _ from 'lodash';
import React, { ReactNode } from 'react';
import { connect } from 'react-redux';
import { match as routerMatch } from 'react-router-dom';
import { Dispatch } from 'redux';
import IBroker from '~Api/Broker/IBroker';
import IDocument from '~Api/Broker/IDocument';
import {
    brokerDocumentUploadAction,
    brokerDocumentsListAction,
    brokerGetAction,
} from '~Brokers/actions';
import BrokerDocumentTypeEnum from '~Api/Broker/BrokerDocumentTypeEnum';
import { brokerDocumentsSelector, brokerSelector } from '~Brokers/selectors';
import { IGlobalState } from '~reducer';
import Layout from './Layout';
import { ColumnsType } from 'antd/lib/table';
import { IDictionary } from '~utilities/IDictionary';
import AfcaMembership from './AfcaMembership';
import AsicSearch from './AsicSearch';
import Insurance from './Insurance';
import Membership from './Membership';
import PhotoId from './PhotoId';

const typeLabels: IDictionary<string> = {
    [BrokerDocumentTypeEnum.AfcaMembership]: 'AFCA Membership',
    [BrokerDocumentTypeEnum.AsicSearch]: 'ACL/CR ASIC Search',
    [BrokerDocumentTypeEnum.CompanySearch]: 'ABN/Company Search ',
    [BrokerDocumentTypeEnum.InsuranceProof]: 'Proof of Professional Indemnity Insurance',
    [BrokerDocumentTypeEnum.MembershipDocument]: 'MFAA/FBAA/Industry Body Membership',
    [BrokerDocumentTypeEnum.PhotoId]: 'Photo ID',
};

const documentTypeComponents: IDictionary<object> = {
    [BrokerDocumentTypeEnum.AfcaMembership]: AfcaMembership,
    [BrokerDocumentTypeEnum.AsicSearch]: AsicSearch,
    [BrokerDocumentTypeEnum.InsuranceProof]: Insurance,
    [BrokerDocumentTypeEnum.MembershipDocument]: Membership,
    [BrokerDocumentTypeEnum.PhotoId]: PhotoId,
};

interface IListDocument {
    hasFile: boolean;
    isUploading: boolean;
    onUpload?: (file: File) => boolean;
    type: BrokerDocumentTypeEnum;
    uuid: string;
}

interface IMatch {
    brokerUuid: string;
}

interface IProps {
    match: routerMatch<IMatch>;
}

interface IPropsSelector {
    broker: IBroker;
    documents: IDocument[];
}

interface IPropsDispatch {
    brokerDocumentUpload: (file: File, type: BrokerDocumentTypeEnum) => void;
    brokerDocumentsList: () => void;
    brokerGet: () => void;
}

type Props = IProps & IPropsSelector & IPropsDispatch;

class Documents extends React.Component<Props> {
    constructor(props: Props) {
        super(props);

        this.onUploadAfcaMembership = this.onUploadAfcaMembership.bind(this);
        this.onUploadAsicSearch = this.onUploadAsicSearch.bind(this);
        this.onUploadCompanySearch = this.onUploadCompanySearch.bind(this);
        this.onUploadInsuranceProof = this.onUploadInsuranceProof.bind(this);
        this.onUploadMembershipDocument = this.onUploadMembershipDocument.bind(this);
        this.onUploadPhotoId = this.onUploadPhotoId.bind(this);
    }

    public componentDidMount() {
        const { broker, documents } = this.props;

        if (!broker) {
            this.props.brokerGet();
        }

        if (!documents) {
            this.props.brokerDocumentsList();
        }
    }

    public render(): JSX.Element {
        const { broker, documents, match } = this.props;

        if (!broker || !documents) {
            return (
                <Layout brokerUuid={match.params.brokerUuid} section='documents'>
                    <Spin />
                </Layout>
            );
        }

        const columns: ColumnsType<IListDocument> = [
            {
                dataIndex: 'type',
                render: (type: BrokerDocumentTypeEnum): ReactNode => {
                    const DocumentComponent: any = documentTypeComponents[type];
                    const documentComponentBlock: JSX.Element = DocumentComponent && (
                        <DocumentComponent broker={broker} />
                    );

                    return (
                        <Space>
                            {typeLabels[type]}
                            {documentComponentBlock}
                        </Space>
                    );
                },
                title: 'Type',
            },
            {
                render: (document: IListDocument): ReactNode => {
                    return (
                        <React.Fragment>
                            <Upload beforeUpload={document.onUpload}>
                                <Tooltip title='Upload'>
                                    <Button type='link'>
                                        {!document.isUploading && <UploadOutlined />}
                                        {document.isUploading && <LoadingOutlined />}
                                    </Button>
                                </Tooltip>
                            </Upload>
                            <Tooltip title='Download'>
                                <Button type='link' disabled={!document.hasFile} href={`${process.env.API_HOST}/broker-documents/${document.uuid}/download`}><CloudDownloadOutlined /></Button>
                            </Tooltip>
                        </React.Fragment>
                    );
                },
                title: 'Actions',
                width: '15%',
            },
        ];

        const afcaMembership: IDocument = _.find(documents, { type: BrokerDocumentTypeEnum.AfcaMembership });
        const asicSearch: IDocument = _.find(documents, { type: BrokerDocumentTypeEnum.AsicSearch });
        const companySearch: IDocument = _.find(documents, { type: BrokerDocumentTypeEnum.CompanySearch });
        const insuranceProof: IDocument = _.find(documents, { type: BrokerDocumentTypeEnum.InsuranceProof });
        const membershipDocument: IDocument = _.find(documents, { type: BrokerDocumentTypeEnum.MembershipDocument });
        const photoId: IDocument = _.find(documents, { type: BrokerDocumentTypeEnum.PhotoId });

        const data: IListDocument[] = [
            {
                hasFile: insuranceProof && !!insuranceProof.uuid,
                isUploading: insuranceProof && insuranceProof.isUploading,
                onUpload: this.onUploadInsuranceProof,
                type: BrokerDocumentTypeEnum.InsuranceProof,
                uuid: insuranceProof && insuranceProof.uuid,
            },
            {
                hasFile: photoId && !!photoId.uuid,
                isUploading: photoId && photoId.isUploading,
                onUpload: this.onUploadPhotoId,
                type: BrokerDocumentTypeEnum.PhotoId,
                uuid: photoId && photoId.uuid,
            },
            {
                hasFile: membershipDocument && !!membershipDocument.uuid,
                isUploading: membershipDocument && membershipDocument.isUploading,
                onUpload: this.onUploadMembershipDocument,
                type: BrokerDocumentTypeEnum.MembershipDocument,
                uuid: membershipDocument && membershipDocument.uuid,
            },
            {
                hasFile: afcaMembership && !!afcaMembership.uuid,
                isUploading: afcaMembership && afcaMembership.isUploading,
                onUpload: this.onUploadAfcaMembership,
                type: BrokerDocumentTypeEnum.AfcaMembership,
                uuid: afcaMembership && afcaMembership.uuid,
            },
            {
                hasFile: asicSearch && !!asicSearch.uuid,
                isUploading: asicSearch && asicSearch.isUploading,
                onUpload: this.onUploadAsicSearch,
                type: BrokerDocumentTypeEnum.AsicSearch,
                uuid: asicSearch && asicSearch.uuid,
            },
            {
                hasFile: companySearch && !!companySearch.uuid,
                isUploading: companySearch && companySearch.isUploading,
                onUpload: this.onUploadCompanySearch,
                type: BrokerDocumentTypeEnum.CompanySearch,
                uuid: companySearch && companySearch.uuid,
            },
        ];

        return (
            <Layout brokerUuid={match.params.brokerUuid} section='documents'>
                <Typography.Title level={2}>Documents</Typography.Title>
                <Table
                    columns={columns}
                    dataSource={data}
                    pagination={false}
                    rowKey='type'
                />
            </Layout>
        );
    }

    private onUploadAfcaMembership(file: File): boolean {
        this.props.brokerDocumentUpload(file, BrokerDocumentTypeEnum.AfcaMembership);

        return false;
    }

    private onUploadAsicSearch(file: File): boolean {
        this.props.brokerDocumentUpload(file, BrokerDocumentTypeEnum.AsicSearch);

        return false;
    }

    private onUploadCompanySearch(file: File): boolean {
        this.props.brokerDocumentUpload(file, BrokerDocumentTypeEnum.CompanySearch);

        return false;
    }

    private onUploadInsuranceProof(file: File): boolean {
        this.props.brokerDocumentUpload(file, BrokerDocumentTypeEnum.InsuranceProof);

        return false;
    }

    private onUploadMembershipDocument(file: File): boolean {
        this.props.brokerDocumentUpload(file, BrokerDocumentTypeEnum.MembershipDocument);

        return false;
    }

    private onUploadPhotoId(file: File): boolean {
        this.props.brokerDocumentUpload(file, BrokerDocumentTypeEnum.PhotoId);

        return false;
    }
}

function mapStateToProps(state: IGlobalState, ownProps: IProps): IPropsSelector {
    return {
        broker: brokerSelector(state, ownProps.match.params.brokerUuid),
        documents: brokerDocumentsSelector(state, ownProps.match.params.brokerUuid),
    };
}

function mapDispatchToProps(dispatch: Dispatch, ownProps: IProps): IPropsDispatch {
    return {
        brokerDocumentUpload: (file: File, type: BrokerDocumentTypeEnum) => dispatch(brokerDocumentUploadAction(ownProps.match.params.brokerUuid, file, type)),
        brokerDocumentsList: () => dispatch(brokerDocumentsListAction(ownProps.match.params.brokerUuid)),
        brokerGet: () => dispatch(brokerGetAction(ownProps.match.params.brokerUuid)),
    };
}

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(Documents);
